<template>
  <div class="lp-choice-chips">
    <lp-label class="lp-choice-chips__label" v-if="label || 'label' in $slots">
      {{ label }}
      <slot v-if="!label" name="label" />
    </lp-label>
    <div
      class="lp-choice-chips__chips"
      :data-ua-unique-id="dataUaUniqueId"
      role="group"
      :aria-label="ariaLabel || label"
    >
      <lp-chip
        v-for="option in options"
        data-ua-multiple-id="ua-choice-chip"
        :key="getTrackBy(option)"
        ref="chipElements"
        :label="getLabelBy(option)"
        :selected="getIsSelected(option)"
        :disabled="option.disabled"
        :icon="getIcon(option)"
        :role="getRole()"
        @update:selected="
          (isSelected: boolean) => onUpdateSelectedChip(option, isSelected)
        "
        @keydown="handleKeydown"
      />
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
  generic="
    Type extends 'single' | 'multiple' = 'single',
    TrackBy extends string = 'value',
    LabelBy extends string = 'label'
  "
>
import { computed, ref, watch } from 'vue'

import LpChip from '@/components/chip/LpChip.vue'
import LpLabel from '@/components/label/LpLabel.vue'

export type TypeOfValues = string | number | boolean

type Option<T extends string, L extends string> = {
  [key in T]: TypeOfValues // TrackBy
} & {
  [key in L]: string // LabelBy
} & {
  disabled?: boolean
}

type CurrentOptionType = Option<TrackBy, LabelBy>

type Props<T extends string = 'value', L extends string = 'label'> = {
  /**
   * Depending on the type, this can be a single value or an array of values.
   */
  modelValue?: Type extends 'single' ? TypeOfValues : TypeOfValues[]
  /**
   * If the type is 'single', the component will only allow one value to be selected.
   * If the type is 'multiple', the component will allow multiple values to be selected.
   */
  type: Type
  /**
   * The label can be a string or customized using a slot `<template #label>`.
   */
  label?: string
  /**
   * The options are the values that the user can select from.
   * Each option should be an object with a value and a label.
   * The value can be any type, but the label should be a string.
   * The trackBy and labelBy properties are used to determine the value and label of the chip option.
   */
  options: Option<T, L>[]
  /**
   * The trackBy property is used to determine the value of the chip option.
   */
  trackBy?: T
  /**
   * The labelBy property is used to determine the label of the chip option.
   */
  labelBy?: L
  /**
   * If true, the component will not warn when the component detects potentially erroneous scenarios.
   */
  noWarn?: boolean
  /**
   * By default, we take the label as the aria label. However, since the label can be a question, we can specify the aria label to specify a bit more what the options are about before the screen reader reads the options.
   */
  ariaLabel?: string
  dataUaUniqueId?: string
}
type Emits = {
  (e: 'update:modelValue', value: Props<TrackBy, LabelBy>['modelValue']): void
}

const debug = false

defineOptions({
  name: 'lp-choice-chips',
})

defineSlots<{
  label(): unknown
}>()

const props = withDefaults(defineProps<Props<TrackBy, LabelBy>>(), {
  modelValue: undefined,
  trackBy: () => 'value' as TrackBy,
  labelBy: () => 'label' as LabelBy,
  noWarn: false,
  dataUaUniqueId: 'ua-choice-chips',
})

const internalModelValue = ref<Props['modelValue']>(props.modelValue)

const emit = defineEmits<Emits>()

watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    if (debug)
      console.log('LpChoiceChip: modelValue changed!', { oldVal, newVal })
    internalModelValue.value = newVal
  },
  { deep: true },
)

const selectedValues = computed((): TypeOfValues[] => {
  if (props.type === 'single') {
    return internalModelValue.value !== undefined
      ? [internalModelValue.value]
      : []
  } else {
    return internalModelValue.value || []
  }
})

const getIsSelected = computed(() => (option: CurrentOptionType) => {
  if (debug) {
    console.log(
      'LpChoiceChip: getIsSelected inner function called for option:',
      getLabelBy(option),
      'selectedValues.value:',
      selectedValues.value,
    )
  }

  const trackByValue = getTrackBy(option)
  if (trackByValue === undefined) {
    if (!(`${props.trackBy as TrackBy}` in option) && !props.noWarn) {
      console.warn(
        `LpChoiceChip: The trackBy value "${props.trackBy as TrackBy}" is not present inside the option. If this is intended and want to turn this message off, set the \`no-warn\` prop to true. If this is not intended, please check the options passed to the component.`,
        { option, trackBy: props.trackBy as TrackBy },
      )
    }
    return false
  }

  return selectedValues.value.includes(trackByValue)
})

const handleChipSelection = (
  isSelected: boolean,
  trackByValue: TypeOfValues,
) => {
  if (debug) {
    console.log('LpChoiceChip: handleChipSelection called.', {
      isSelected,
      trackByValue,
      currentModelValue: props.modelValue,
    })
  }
  if (props.type === 'single') {
    // @ts-expect-error TS not detecting we're in the single type
    emit('update:modelValue', isSelected ? trackByValue : undefined)
    internalModelValue.value = isSelected ? trackByValue : undefined

    if (debug) {
      console.log(
        'LpChoiceChip: Emitted update:modelValue (single). New value:',
        isSelected ? trackByValue : undefined,
      )
    }
  } else {
    let currentValues =
      (internalModelValue.value as TypeOfValues[] | undefined) || []
    if (!Array.isArray(currentValues)) {
      currentValues = []
    }

    if (isSelected) {
      currentValues = [...currentValues, trackByValue]
    } else {
      currentValues = currentValues.filter((value) => value !== trackByValue)
    }

    emit(
      'update:modelValue',
      currentValues as Props<TrackBy, LabelBy>['modelValue'],
    )
    internalModelValue.value = currentValues

    if (debug) {
      console.log(
        'LpChoiceChip: Emitted update:modelValue (multiple). New value:',
        currentValues,
      )
    }
  }
}

const chipElements = ref<InstanceType<typeof LpChip>[]>([])

function handleKeydown(event: KeyboardEvent) {
  if (!chipElements.value) return

  if (debug) {
    console.log(
      'LpChoiceChip: handleKeydown called for event:',
      event.key,
      'selectedValues.value:',
      selectedValues.value,
      chipElements.value,
    )
  }

  const currentIndex = chipElements.value.findIndex(
    (chip) => chip.$el === document.activeElement,
  )

  if (currentIndex === -1) return

  let nextIndex = currentIndex

  const getNextIndex = (direction: 1 | -1) => {
    if (!chipElements.value) return

    let found = false
    let index = currentIndex
    while (!found) {
      index =
        direction > 0
          ? index + 1 >= chipElements.value.length
            ? 0
            : index + 1
          : index - 1 < 0
            ? chipElements.value.length - 1
            : index - 1
      const chip = chipElements.value[index]
      if (!chip.$props.disabled || index === currentIndex) {
        nextIndex = index
        found = true
      }
      if (index === currentIndex) break
    }
  }

  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      getNextIndex(1)
      break
    case 'ArrowLeft':
    case 'ArrowUp':
      getNextIndex(-1)
      break
    default:
      return
  }

  if (chipElements.value[nextIndex]) {
    chipElements.value[nextIndex].$el.focus()
  }

  event.preventDefault()
}

const getTrackBy = (option: CurrentOptionType) => option[props.trackBy]
const getLabelBy = (option: CurrentOptionType) => option[props.labelBy]

const getIcon = (option: CurrentOptionType) =>
  props.type === 'multiple' && getIsSelected.value(option) ? 'check' : undefined
const getRole = () => (props.type === 'single' ? 'radio' : 'checkbox')

const onUpdateSelectedChip = (
  option: CurrentOptionType,
  isSelected: boolean,
) => {
  if (debug) {
    console.log(
      'LpChoiceChip: onUpdateSelectedChip called for option:',
      getLabelBy(option),
      'selectedValues.value:',
      selectedValues.value,
    )
  }
  handleChipSelection(isSelected, getTrackBy(option))
}
</script>
